class CustomCsvTable extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    this.csvUrl = (this.dataset.csvUrl || '').trim();
    this.delimiterMode = this.dataset.csvDelimiter || 'auto';
    this.loadingLabel = this.dataset.loadingLabel || 'Loading table...';
    this.errorLabel = this.dataset.errorLabel || 'The table could not be displayed.';
    this.emptyLabel = this.dataset.emptyLabel || 'Add a CSV URL to display a table.';
    this.downloadLabel = this.dataset.downloadLabel || 'Download CSV';

    this.loadingElement = this.querySelector('[data-csv-loading]');
    this.noticeElement = this.querySelector('[data-csv-notice]');
    this.noticeTextElement = this.querySelector('[data-csv-notice-text]');
    this.downloadElement = this.querySelector('[data-csv-download]');
    this.tableWrapElement = this.querySelector('[data-csv-table-wrap]');
    this.tableElement = this.querySelector('[data-csv-table]');

    if (!this.csvUrl) {
      this.showNotice(this.emptyLabel, false);
      return;
    }

    this.loadTable();
  }

  async loadTable() {
    this.showLoading();

    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => controller.abort(), 10000);

    try {
      const response = await fetch(this.csvUrl, {
        credentials: 'omit',
        mode: 'cors',
        signal: controller.signal,
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const csvText = await response.text();
      const delimiter = this.resolveDelimiter(csvText, this.delimiterMode);
      const rows = this.normalizeRows(this.parseCsv(csvText, delimiter));

      if (rows.length === 0) {
        throw new Error('Empty CSV');
      }

      this.renderTable(rows);
      this.hideFeedback();
    } catch (error) {
      this.showNotice(this.errorLabel, true);
    } finally {
      window.clearTimeout(timeoutId);
      this.hideLoading();
    }
  }

  resolveDelimiter(csvText, mode) {
    if (mode === 'semicolon') return ';';
    if (mode === 'comma') return ',';

    const sampleLines = String(csvText)
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter(Boolean)
      .slice(0, 5);

    let semicolonScore = 0;
    let commaScore = 0;

    for (const line of sampleLines) {
      semicolonScore += this.countUnquotedCharacters(line, ';');
      commaScore += this.countUnquotedCharacters(line, ',');
    }

    return semicolonScore > commaScore ? ';' : ',';
  }

  countUnquotedCharacters(line, character) {
    let count = 0;
    let insideQuotes = false;

    for (let index = 0; index < line.length; index += 1) {
      const currentCharacter = line[index];

      if (currentCharacter === '"') {
        const nextCharacter = line[index + 1];
        if (insideQuotes && nextCharacter === '"') {
          index += 1;
          continue;
        }

        insideQuotes = !insideQuotes;
        continue;
      }

      if (!insideQuotes && currentCharacter === character) {
        count += 1;
      }
    }

    return count;
  }

  parseCsv(csvText, delimiter) {
    const rows = [];
    let row = [];
    let value = '';
    let insideQuotes = false;

    for (let index = 0; index < csvText.length; index += 1) {
      const currentCharacter = csvText[index];
      const nextCharacter = csvText[index + 1];

      if (currentCharacter === '"') {
        if (insideQuotes && nextCharacter === '"') {
          value += '"';
          index += 1;
        } else {
          insideQuotes = !insideQuotes;
        }
        continue;
      }

      if (!insideQuotes && currentCharacter === delimiter) {
        row.push(value.trim());
        value = '';
        continue;
      }

      if (!insideQuotes && (currentCharacter === '\n' || currentCharacter === '\r')) {
        if (currentCharacter === '\r' && nextCharacter === '\n') {
          index += 1;
        }

        row.push(value.trim());
        rows.push(row);
        row = [];
        value = '';
        continue;
      }

      value += currentCharacter;
    }

    if (value.length > 0 || row.length > 0) {
      row.push(value.trim());
      rows.push(row);
    }

    return rows;
  }

  normalizeRows(rows) {
    const filteredRows = rows.filter((row) => row.some((cell) => cell !== ''));

    if (filteredRows.length === 0) return [];

    const columnCount = filteredRows.reduce((max, row) => Math.max(max, row.length), 0);

    return filteredRows.map((row) => {
      const normalizedRow = row.slice();

      while (normalizedRow.length < columnCount) {
        normalizedRow.push('');
      }

      return normalizedRow;
    });
  }

  renderTable(rows) {
    if (!this.tableElement) return;

    const tableHead = this.tableElement.querySelector('thead');
    const tableBody = this.tableElement.querySelector('tbody');

    if (!tableHead || !tableBody) return;

    tableHead.innerHTML = '';
    tableBody.innerHTML = '';

    const [headerRow, ...bodyRows] = rows;
    const headRowElement = document.createElement('tr');

    headerRow.forEach((cellValue) => {
      const cell = document.createElement('th');
      cell.scope = 'col';
      cell.textContent = cellValue;
      headRowElement.appendChild(cell);
    });

    tableHead.appendChild(headRowElement);

    bodyRows.forEach((row) => {
      const rowElement = document.createElement('tr');

      row.forEach((cellValue, cellIndex) => {
        const cell = document.createElement(cellIndex === 0 ? 'th' : 'td');
        if (cellIndex === 0) cell.scope = 'row';
        cell.textContent = cellValue;
        rowElement.appendChild(cell);
      });

      tableBody.appendChild(rowElement);
    });

    this.tableElement.hidden = false;

    if (this.tableWrapElement) {
      this.tableWrapElement.hidden = false;
    }
  }

  showLoading() {
    if (this.loadingElement) {
      this.loadingElement.hidden = false;
      this.loadingElement.textContent = this.loadingLabel;
    }

    if (this.tableElement) {
      this.tableElement.hidden = true;
    }

    if (this.noticeElement) {
      this.noticeElement.hidden = true;
    }
  }

  hideLoading() {
    if (this.loadingElement) {
      this.loadingElement.hidden = true;
    }
  }

  hideFeedback() {
    if (this.noticeElement) {
      this.noticeElement.hidden = true;
    }

    if (this.downloadElement) {
      this.downloadElement.hidden = true;
    }
  }

  showNotice(message, includeDownloadLink) {
    if (this.noticeElement) {
      this.noticeElement.hidden = false;
    }

    if (this.noticeTextElement) {
      this.noticeTextElement.textContent = message;
    }

    if (this.downloadElement) {
      if (includeDownloadLink && this.csvUrl) {
        this.downloadElement.hidden = false;
        this.downloadElement.href = this.csvUrl;
        this.downloadElement.textContent = this.downloadLabel;
      } else {
        this.downloadElement.hidden = true;
      }
    }

    if (this.tableElement) {
      this.tableElement.hidden = true;
    }

    if (this.tableWrapElement) {
      this.tableWrapElement.hidden = true;
    }
  }
}

if (!customElements.get('custom-csv-table')) {
  customElements.define('custom-csv-table', CustomCsvTable);
}
